import React from "react";
import { Accordion, AccordionItem as Item } from "@szhsin/react-accordion";

export type FaqItem = {
  question: React.ReactNode;
  answer: React.ReactNode;
};

type FaqAccordionProps = {
  items: FaqItem[];
  columns?: 1 | 2; // desktop columns
  allowMultiple?: boolean; // open multiple at once
};

const PlusMinusIcon = ({ open }: { open: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    className="text-zinc-700 dark:text-zinc-200"
    aria-hidden="true"
  >
    {/* horizontal line */}
    <path
      d="M5 12h14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* vertical line (hidden when open -> minus) */}
    {!open && (
      <path
        d="M12 5v14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

export default function FaqAccordion({
  items,
  columns = 2,
  allowMultiple = true,
}: FaqAccordionProps) {
  return (
    <section className="w-full">
      <div
        className={[
          "grid gap-5",
          columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
        ].join(" ")}
      >
        {items.map((it, idx) => (
          <Accordion
            key={idx}
            transition
            transitionTimeout={250}
            allowMultiple={allowMultiple}
            className="w-full"
          >
            <Item
              className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/40"
              header={({ state: { isEnter } }) => (
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <div className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                    {it.question}
                  </div>

                  {/* The right “+ / -” control */}
                  <span
                    className={[
                      "flex h-9 w-9 items-center justify-center rounded-xl",
                      "border border-zinc-200 bg-zinc-50",
                      "dark:border-zinc-800 dark:bg-zinc-900/60",
                      "transition-colors",
                      isEnter ? "bg-zinc-100 dark:bg-zinc-900" : "",
                    ].join(" ")}
                  >
                    <PlusMinusIcon open={isEnter} />
                  </span>
                </div>
              )}
              buttonProps={{
                className: () =>
                  [
                    "w-full text-left",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
                    "rounded-2xl",
                  ].join(" "),
              }}
              panelProps={{
                className:
                  "px-5 pb-5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-200",
              }}
              contentProps={{
                className: "transition-height duration-250 ease-out",
              }}
            >
              {it.answer}
            </Item>
          </Accordion>
        ))}
      </div>
    </section>
  );
}
